import { useCallback, useEffect, useState } from 'react';

import { Alert, Badge, Button, HelpTip, Input, Select } from '@/components/ui';
import { api } from '@/lib/api';

/**
 * 🔴 "QUEM RECEBE O QUÊ" + a SIMULAÇÃO — a razão de a rodada existir
 * (Frente C): sem isto, o roteamento multigrupo é um multiplicador de erro.
 *
 *   · As DUAS direções — por cliente (que grupos receberiam algo dele) e por
 *     grupo (de quais clientes ele recebe). Ver só um lado esconde metade dos
 *     erros.
 *   · A simulação: escolher um cliente e um aviso e ver EXATAMENTE os grupos
 *     que receberiam, **sem enviar nada** — o único jeito de conferir 50
 *     grupos sem descobrir o erro pelo cliente ligando. Mensagem enviada em
 *     grupo de cliente não se apaga.
 */

interface Roteamento {
  porCliente: Array<{
    sistema: string;
    codigo: string;
    nome: string | null;
    grupos: Array<{ grupoId: string; nome: string; conferido: boolean }>;
  }>;
  porGrupo: Array<{
    grupoId: string;
    nome: string;
    interno: boolean;
    papel: string | null;
    recebeDe: string | null;
    clientes: Array<{ sistema: string; codigo: string; nome: string | null; conferido: boolean }>;
  }>;
  marcadosSemGrupo: Array<{ clienteId: string; nome: string }>;
  /** Marcado para receber, COM grupo — e o bot fora dele (09/09): o envio
   *  falharia. É a lista que troca 50 telefonemas por um progresso. */
  marcadosComGrupoSemBot?: Array<{ clienteId: string; nome: string }>;
}

interface Simulacao {
  cliente: { nome: string | null };
  grupos: Array<{
    grupoId: string;
    nome: string;
    porSupervisao: boolean;
    /** Entrou pelo TIPO da tarefa (mig 576 — ex.: o grupo da Route). */
    porTipo?: boolean;
    comNomeDoCliente: boolean;
    receberia: boolean;
    explicacao: string | null;
  }>;
  receberiam: number;
}

export function RoteamentoWhatsApp({ gatilhos }: { gatilhos: Array<{ chave: string; nome: string }> }) {
  const [aberto, setAberto] = useState(false);
  const [dados, setDados] = useState<Roteamento | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [direcao, setDirecao] = useState<'cliente' | 'grupo'>('cliente');

  const [simCliente, setSimCliente] = useState('');
  const [simGatilho, setSimGatilho] = useState('');
  /** Opcional (mig 576): simular a OS COM o tipo — é o que mostra a Route. */
  const [simTipo, setSimTipo] = useState('');
  const [simulacao, setSimulacao] = useState<Simulacao | null>(null);

  const carregar = useCallback(() => {
    setErro(null);
    api
      .get<Roteamento>('/api/v1/admin/whatsapp/roteamento')
      .then(setDados)
      .catch((e: Error) => setErro(e.message));
  }, []);

  useEffect(() => {
    if (aberto) carregar();
  }, [aberto, carregar]);

  async function simular() {
    setErro(null);
    setSimulacao(null);
    const [sistema, codigo] = simCliente.split('|');
    if (!sistema || !codigo || !simGatilho) return;
    try {
      const r = await api.post<Simulacao>('/api/v1/admin/whatsapp/simulacao', {
        gatilho: simGatilho,
        sistema,
        codigo,
        ...(simTipo.trim() ? { tipoTarefa: simTipo.trim() } : {}),
      });
      setSimulacao(r);
    } catch (err) {
      setErro((err as Error).message);
    }
  }

  return (
    <div className="border-t border-line pt-4 dark:border-line-dark" data-testid="wa-roteamento">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-sm font-medium text-ink-1 dark:text-ink-dark-1">
          Quem recebe o quê
          <HelpTip texto="As duas direções do roteamento: por cliente (que grupos receberiam algo dele) e por grupo (de quais clientes ele recebe). E a simulação: escolha um cliente e um aviso e veja exatamente quais grupos receberiam — sem enviar nada. Confira aqui ANTES de ligar qualquer aviso." />
        </span>
        <Button size="sm" variant="ghost" onClick={() => setAberto(!aberto)} data-testid="wa-roteamento-abrir">
          {aberto ? 'Recolher' : 'Ver e simular'}
        </Button>
      </div>

      {aberto && (
        <div className="mt-3 space-y-4">
          {erro && <Alert variant="danger">{erro}</Alert>}

          {/* ─── 🔴 A SIMULAÇÃO — antes de ligar qualquer coisa ─── */}
          <div className="rounded-lg border border-line p-3 dark:border-line-dark" data-testid="wa-simulacao">
            <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
              Simular um aviso — sem enviar nada
            </p>
            <div className="mt-2 flex flex-wrap items-end gap-2">
              <Select
                value={simCliente}
                onChange={(e) => setSimCliente(e.target.value)}
                data-testid="wa-sim-cliente"
                className="max-w-xs"
              >
                <option value="">Escolha o cliente…</option>
                {dados?.porCliente.map((c) => (
                  <option key={`${c.sistema}|${c.codigo}`} value={`${c.sistema}|${c.codigo}`}>
                    {c.nome ?? c.codigo}
                    {c.sistema === 'auvo' ? ' (do Auvo)' : ''}
                  </option>
                ))}
              </Select>
              <Select
                value={simGatilho}
                onChange={(e) => setSimGatilho(e.target.value)}
                data-testid="wa-sim-gatilho"
                className="max-w-xs"
              >
                <option value="">…e o aviso</option>
                {gatilhos.map((g) => (
                  <option key={g.chave} value={g.chave}>
                    {g.nome}
                  </option>
                ))}
              </Select>
              <Input
                value={simTipo}
                onChange={(e) => setSimTipo(e.target.value)}
                placeholder={'Tipo da tarefa (opcional) — ex.: MANUTENÇÃO CORRETIVA - CFTV - ROUTE'}
                data-testid="wa-sim-tipo"
                className="max-w-sm"
                title="Só os avisos de OS têm tipo de tarefa. Com um tipo que contém o marcador de um grupo (ex.: - Route), a simulação mostra esse grupo recebendo também."
              />
              <Button size="sm" onClick={() => void simular()} data-testid="wa-simular">
                Simular
              </Button>
            </div>

            {simulacao && (
              <div className="mt-3 space-y-1 text-sm" data-testid="wa-sim-resultado">
                <p className="text-ink-2 dark:text-ink-dark-2">
                  {simulacao.receberiam === 0
                    ? 'Nenhum grupo receberia — com a configuração de agora, esta mensagem não sai.'
                    : simulacao.receberiam === 1
                      ? '1 grupo receberia:'
                      : `${simulacao.receberiam} grupos receberiam:`}
                </p>
                <ul className="space-y-1">
                  {simulacao.grupos.map((g) => (
                    <li key={g.grupoId} className="flex flex-wrap items-center gap-2">
                      <Badge variant={g.receberia ? 'success' : 'neutral'} size="sm">
                        {g.receberia ? 'recebe' : 'não recebe'}
                      </Badge>
                      <span className="text-ink-1 dark:text-ink-dark-1">{g.nome}</span>
                      {g.porSupervisao && <Badge variant="neutral" size="sm">supervisão</Badge>}
                      {g.porTipo && <Badge variant="info" size="sm">pelo tipo da tarefa</Badge>}
                      {g.receberia && g.comNomeDoCliente && (
                        <span className="text-xs text-ink-3 dark:text-ink-dark-3">
                          com o nome do cliente na frente
                        </span>
                      )}
                      {!g.receberia && g.explicacao && (
                        <span className="text-xs text-ink-3 dark:text-ink-dark-3">{g.explicacao}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* ─── As duas direções ─── */}
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={direcao === 'cliente' ? 'secondary' : 'ghost'}
              onClick={() => setDirecao('cliente')}
              data-testid="wa-dir-cliente"
            >
              Por cliente
            </Button>
            <Button
              size="sm"
              variant={direcao === 'grupo' ? 'secondary' : 'ghost'}
              onClick={() => setDirecao('grupo')}
              data-testid="wa-dir-grupo"
            >
              Por grupo
            </Button>
          </div>

          {dados && direcao === 'cliente' && (
            <div className="space-y-1 text-sm" data-testid="wa-por-cliente">
              {dados.marcadosSemGrupo.length > 0 && (
                <Alert variant="warning" data-testid="wa-marcados-sem-grupo">
                  {dados.marcadosSemGrupo.length === 1
                    ? `${dados.marcadosSemGrupo[0].nome} está marcado para receber avisos e não tem grupo nenhum.`
                    : `${dados.marcadosSemGrupo.length} clientes estão marcados para receber avisos e não têm grupo nenhum.`}{' '}
                  {/* 🔴 13/09: a frase apontava para o "destino de sobra", que foi
                      removido. Ela agora diz o que de fato acontece — e onde
                      olhar. Texto que aponta para um lugar muda com o lugar. */}
                  O aviso deles não vai para lugar nenhum: fica registrado na trilha, com o
                  motivo. Ligue um grupo a cada um em <strong>Vínculos</strong> para que passem a
                  receber.
                </Alert>
              )}
              {(dados.marcadosComGrupoSemBot?.length ?? 0) > 0 && (
                <Alert variant="warning" data-testid="wa-marcados-sem-bot">
                  {dados.marcadosComGrupoSemBot!.length === 1
                    ? `${dados.marcadosComGrupoSemBot![0].nome} tem grupo mapeado, mas o número do bot NÃO está dentro dele`
                    : `${dados.marcadosComGrupoSemBot!.length} clientes têm grupo mapeado, mas o número do bot NÃO está dentro`}{' '}
                  — nenhum envio chega até o administrador do grupo readicionar o número. Peça a
                  inclusão e use “Buscar grupos do WhatsApp” no cartão do WhatsApp para reconferir
                  esta lista: {dados.marcadosComGrupoSemBot!.map((c) => c.nome).join(' · ')}
                </Alert>
              )}
              {dados.porCliente.map((c) => (
                <p key={`${c.sistema}:${c.codigo}`} className="text-ink-2 dark:text-ink-dark-2">
                  <span className="font-medium text-ink-1 dark:text-ink-dark-1">{c.nome ?? c.codigo}</span>
                  {c.sistema === 'auvo' ? ' (do Auvo)' : ''} →{' '}
                  {c.grupos.map((g) => g.nome + (g.conferido ? '' : ' (proposta)')).join(' · ')}
                </p>
              ))}
              {dados.porCliente.length === 0 && (
                <p className="text-ink-3 dark:text-ink-dark-3">Nenhum cliente vinculado a grupo ainda.</p>
              )}
            </div>
          )}

          {dados && direcao === 'grupo' && (
            <div className="space-y-1 text-sm" data-testid="wa-por-grupo">
              {dados.porGrupo.map((g) => (
                <p key={g.grupoId} className="text-ink-2 dark:text-ink-dark-2">
                  <span className="font-medium text-ink-1 dark:text-ink-dark-1">{g.nome}</span> ←{' '}
                  {g.recebeDe ??
                    g.clientes.map((c) => (c.nome ?? c.codigo) + (c.conferido ? '' : ' (proposta)')).join(' · ')}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
